import { type FC } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

const AVATAR_BACKGROUND_COLOR_CLASSES = [
  'bg-red-500 dark:bg-red-500',
  'bg-orange-500 dark:bg-orange-500',
  'bg-amber-500 dark:bg-amber-500',
  'bg-lime-600 dark:bg-lime-600',
  'bg-green-600 dark:bg-green-600',
  'bg-emerald-600 dark:bg-emerald-600',
  'bg-teal-600 dark:bg-teal-600',
  'bg-cyan-600 dark:bg-cyan-600',
  'bg-sky-600 dark:bg-sky-600',
  'bg-blue-600 dark:bg-blue-600',
  'bg-indigo-600 dark:bg-indigo-600',
  'bg-violet-600 dark:bg-violet-600',
  'bg-fuchsia-600 dark:bg-fuchsia-600',
  'bg-pink-600 dark:bg-pink-600',
  'bg-rose-600 dark:bg-rose-600',
];

export const getUserInitials = (value: string) => {
  const words = value.trim().split(/\s+/).filter(Boolean);

  if (words.length === 0) {
    return 'NA';
  }

  return words
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
};

const hashToIndex = (value: string, length: number) => {
  if (!value) {
    return 0;
  }

  let hash = 5381;

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 33) ^ value.charCodeAt(index);
  }

  return Math.abs(hash) % length;
};

const getAvatarBackgroundClassName = (seed: string) =>
  AVATAR_BACKGROUND_COLOR_CLASSES[hashToIndex(seed, AVATAR_BACKGROUND_COLOR_CLASSES.length)];

export const UserAvatar: FC<{
  className?: string;
  fallbackClassName?: string;
  user: {
    id?: string | null;
    name?: string | null;
    picture?: string | null;
  };
}> = ({ className, fallbackClassName, user: avatarUser }) => {
  const avatarName = avatarUser?.name ?? '';
  const avatarSeed = avatarUser?.id ?? avatarName;

  return (
    <Avatar className={className}>
      {avatarUser?.picture ? (
        <AvatarImage src={avatarUser?.picture} />
      ) : (
        avatarName && (
          <AvatarFallback
            className={cn(
              'text-[10px] font-semibold text-text-inverted',
              avatarSeed === 'Deleted'
                ? 'bg-neutral-950 text-white dark:bg-neutral-950'
                : getAvatarBackgroundClassName(avatarSeed),
              fallbackClassName,
            )}
          >
            {getUserInitials(avatarName)}
          </AvatarFallback>
        )
      )}
    </Avatar>
  );
};
